// src/components/layout/TopHeader.tsx
// Renamed from TopNav.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import './TopHeader.scss'; // Rename SCSS file too
import { AppUser, UserRole } from '../../types'; // Adjust path
// Import icons if needed
// import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
// import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
// Removed Feedback icon import
import { Bell, Bug, CircleUser, Flame, HelpCircle, LogOut, Menu, Settings2, UserCircle, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Driver, DriveStep } from 'driver.js';
import FeedbackModal from '../feedback/FeedbackModal';
import { useAuth } from '../../contexts/AuthContext';
import { useRolePlay } from '../../contexts/RolePlayContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { Notification } from '../../services/notificationService';
import { fetchMyGamification } from '../../services/gamificationApi';
import BadgeUnlockPopup from '../gamification/BadgeUnlockPopup';
import { useTutorial } from '../tutorial/useTutorial';
import { translateNotificationMessage } from '../../utils/notificationMessage';
import DemoProxySwitcher from './DemoProxySwitcher';
import { getReferenceSteps } from '../tutorial/referenceSteps';
import { getPageSteps } from '../tutorial/pageSteps';

// Routes gated behind ProtectedRoute's/RoleProtectedRoute's `blockTrial` in App.tsx
// (mirrors backend/app/communication/constants.py's route set) — a trial user
// hitting one of these gets silently bounced to "/", so a notification link
// pointing here is suppressed for the current viewer instead of followed.
const TRIAL_BLOCKED_ROUTES = new Set(['/my-progress', '/competitions']);

interface TopHeaderProps {
    currentUser: AppUser | null;
    onLogout: () => Promise<void>;
    // Removed onToggleFeedback
    // Mobile off-canvas sidebar drawer (see Layout.tsx) — the button below is
    // only shown ≤768px via CSS, and hidden entirely when there's no sidebar.
    isMobileSidebarOpen: boolean;
    onToggleMobileSidebar: () => void;
    hideSidebarToggle?: boolean;
}

const getRoleLabel = (role: UserRole | undefined, t: TFunction) => {
    switch (role) {
        case "super_admin": return t('users.roleSuperAdmin');
        case "admin": return t('users.roleAdministrator');
        case "trainer": return t('common.trainer');
        case "trainee": return t('common.trainee');
        default: return t('common.user');
    }
};

// Path segments whose breadcrumb reads the same as a translated nav label. Anything
// else keeps the Title Case English default below until its page is translated.
const BREADCRUMB_LABEL_KEYS: Record<string, string> = {
    roleplay: 'sidebar.rolePlay',
    // The page's place in the reporting flow, not the sidebar's "Feedback" (UX audit).
    sessions: 'feedback.sessionHistory',
    'my-assignments': 'sidebar.myAssignments',
    classes: 'sidebar.classes',
    'my-classes': 'sidebar.myClasses',
    'my-practice': 'sidebar.myPractice',
    'my-grades': 'sidebar.myGrades',
    'my-progress': 'sidebar.myProgress',
    competitions: 'sidebar.competitions',
    assignments: 'sidebar.assignments',
    reviews: 'sidebar.reviews',
    bundles: 'sidebar.bundles',
    'team-progress': 'sidebar.teamProgress',
    users: 'sidebar.users',
    'join-requests': 'sidebar.joinRequests',
    dashboard: 'sidebar.dashboard',
    characters: 'sidebar.characters',
    'prompt-studio': 'sidebar.promptStudio',
    'scene-editor': 'sidebar.sceneEditor',
    organizations: 'sidebar.organizations',
    reports: 'sidebar.reports',
    feedback: 'sidebar.feedback',
    'my-reviews': 'sidebar.myReviews',
    'reviewer-dashboard': 'myReviews.pageTitle',
    'platform-dashboard': 'sidebar.platformOverview',
    profile: 'header.crumbProfile',
    'device-setup': 'onboarding.deviceSetup',
    subscription: 'header.crumbSubscription',
    'getting-started': 'header.crumbGettingStarted',
    'payment-success': 'header.crumbPaymentSuccess',
};

const getBreadcrumbItems = (pathname: string, t: TFunction) => {
    const pathParts = pathname.split('/').filter(Boolean);

    // "/" is the Home dashboard (Role Play lives at "/roleplay")
    if (pathParts.length === 0) {
        return [
            { label: t('sidebar.home'), path: '/' }
        ];
    }

    const items = [];
    let currentPath = '';

    // UUID pattern — skip these segments (detail pages handle their own breadcrumb)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    for (const part of pathParts) {
        currentPath += `/${part}`;

        // Skip UUID segments — detail pages show their own context
        if (uuidPattern.test(part)) continue;

        // Default: kebab-case segment → Title Case ("my-progress" → "My Progress")
        let label = part
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        // Known segments use the translated nav label. That also covers the one the
        // Title Case default gets wrong: "roleplay" is one word in the path but two
        // in the product's name.
        const labelKey = BREADCRUMB_LABEL_KEYS[part];
        if (labelKey) label = t(labelKey);

        items.push({ label, path: currentPath });
    }

    return items;
};

const formatNotificationTime = (createdAt: string, t: TFunction, locale: string): string => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now.getTime() - created.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 60) return t('header.minutesAgo', { n: Math.max(1, diffMin) });
    if (diffHrs < 24) return t('header.hoursAgo', { n: diffHrs });
    if (diffDays < 7) return created.toLocaleDateString(locale, { weekday: 'short' });
    return created.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
};

const TopHeader: React.FC<TopHeaderProps> = ({
    currentUser,
    onLogout,
    isMobileSidebarOpen,
    onToggleMobileSidebar,
    hideSidebarToggle = false,
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false);
    const [isNotifOpen, setIsNotifOpen] = useState<boolean>(false);
    const [isTutorialMenuOpen, setIsTutorialMenuOpen] = useState<boolean>(false);
    const [streakCount, setStreakCount] = useState<number | null>(null);
    const [badgeQueue, setBadgeQueue] = useState<{ id: string; key: string; name: string }[]>([]);
    const knownBadgeKeysRef = useRef<Set<string> | null>(null);
    const { token, isTrialUser } = useAuth();
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const notifRef = useRef<HTMLDivElement>(null);
    const notifButtonRef = useRef<HTMLButtonElement>(null);
    const tutorialMenuRef = useRef<HTMLDivElement>(null);
    const tutorialButtonRef = useRef<HTMLButtonElement>(null);
    const pageTourDriverRef = useRef<Driver | null>(null);
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const breadcrumbItems = getBreadcrumbItems(location.pathname, t);
    const { isMarketplaceUser, userRole, realRole, isProxyActive } = useAuth();
    // The "My Profile" entry surfaces the marketplace billing/subscription view, so
    // only the purchaser (trainer) or an admin of a marketplace org sees it; manual/
    // Stripe orgs reach their own profile page through their own layout.
    const canSeeSubscription = isMarketplaceUser && (userRole === 'trainer' || userRole === 'admin');
    const { rolePlaySelections, componentDetails } = useRolePlay();
    const { startReferenceTour } = useTutorial();
    // Computed fresh each time the dropdown is opened (not memoized on
    // location.pathname) — several pages render their tour-anchor DOM ids
    // asynchronously (after data loads) or via in-page state changes (tab
    // switches, an inline editor opening/closing) with no route change, so a
    // route-keyed memo can freeze on a stale (often empty) snapshot forever.
    const [pageTourSteps, setPageTourSteps] = useState<DriveStep[]>([]);
    const notifListRef = useRef<HTMLDivElement>(null);
    const { notifications, unreadCount, loading, loadingMore, hasMore, loadNotifications, loadMoreNotifications, markAsRead, markAllAsRead } = useNotifications();

    // Close menus if clicked outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                menuRef.current && !menuRef.current.contains(event.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
            if (
                notifRef.current && !notifRef.current.contains(event.target as Node) &&
                notifButtonRef.current && !notifButtonRef.current.contains(event.target as Node)
            ) {
                setIsNotifOpen(false);
            }
            if (
                tutorialMenuRef.current && !tutorialMenuRef.current.contains(event.target as Node) &&
                tutorialButtonRef.current && !tutorialButtonRef.current.contains(event.target as Node)
            ) {
                setIsTutorialMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Escape closes whichever header menu is open and hands focus back to its
    // trigger, so keyboard users aren't left inside a menu they can't dismiss.
    useEffect(() => {
        if (!isMenuOpen && !isNotifOpen && !isTutorialMenuOpen) return;
        function handleEscape(event: KeyboardEvent) {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            if (isMenuOpen) { setIsMenuOpen(false); buttonRef.current?.focus(); }
            if (isNotifOpen) { setIsNotifOpen(false); notifButtonRef.current?.focus(); }
            if (isTutorialMenuOpen) { setIsTutorialMenuOpen(false); tutorialButtonRef.current?.focus(); }
        }
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isMenuOpen, isNotifOpen, isTutorialMenuOpen]);

    // Refetch gamification state (streak + badges) whenever there's a real reason
    // to expect it changed, instead of a tight fixed-interval poll. /gamification/me
    // is Redis-cached for 120s server-side, so these calls are cheap even when frequent.
    const pollGamification = useCallback(() => {
        if (!token || isTrialUser) return;
        fetchMyGamification(token)
            .then((data) => {
                setStreakCount(data.streak_current);

                const currentKeys = new Set(data.badges.map((b) => b.key));
                if (knownBadgeKeysRef.current) {
                    const newlyEarned = data.badges.filter((b) => !knownBadgeKeysRef.current!.has(b.key));
                    if (newlyEarned.length > 0) {
                        setBadgeQueue((prev) => [
                            ...prev,
                            ...newlyEarned.map((b) => ({
                                id: `${b.key}-${b.awarded_at}`,
                                key: b.key,
                                name: b.name,
                            })),
                        ]);
                    }
                }
                knownBadgeKeysRef.current = currentKeys;
            })
            .catch(() => {/* non-fatal */});
    }, [token, isTrialUser]);

    // Trigger 1: on mount and on every route change (navigating away from an active
    // roleplay session is the most common moment gamification state just changed).
    useEffect(() => {
        pollGamification();
    }, [pollGamification, location.pathname]);

    // Trigger 2: event-driven — tab refocus, an explicit "session finished processing"
    // signal (dispatched by useSessionDetail when feedback_status completes), plus a
    // long-interval fallback in case both of the above are missed on a given page.
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') pollGamification();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('gamification:refresh', pollGamification);
        const fallbackIntervalId = setInterval(pollGamification, 20000);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('gamification:refresh', pollGamification);
            clearInterval(fallbackIntervalId);
        };
    }, [pollGamification]);

    // Infinite scroll: load next page when user scrolls within 80px of the bottom
    useEffect(() => {
        const listEl = notifListRef.current;
        if (!listEl) return;

        function handleScroll() {
            if (!listEl) return;
            const nearBottom = listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight < 80;
            if (nearBottom && hasMore && !loadingMore) {
                loadMoreNotifications();
            }
        }

        listEl.addEventListener('scroll', handleScroll);
        return () => listEl.removeEventListener('scroll', handleScroll);
    }, [hasMore, loadingMore, loadMoreNotifications, isNotifOpen]);

    const handleBellClick = () => {
        const opening = !isNotifOpen;
        setIsNotifOpen(opening);
        if (opening) {
            // Always reload from page 1 when re-opening so the list is fresh
            loadNotifications();
        }
    };

    const handleNotifClick = (notif: Notification) => {
        if (!notif.read) {
            markAsRead(notif.id);
        }
        // Links are persisted independent of trial status (so they don't go stale
        // once a trial user upgrades) — routes behind ProtectedRoute's blockTrial
        // are gated here instead, against the viewer's *current* trial status.
        if (notif.link && !(isTrialUser && TRIAL_BLOCKED_ROUTES.has(notif.link))) {
            setIsNotifOpen(false);
            navigate(notif.link);
        }
    };

    const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);
    // The visual badge is aria-hidden, so the bell's own name carries the count.
    const bellLabel = unreadCount > 0 ? t('header.notificationsUnread', { count: unreadCount }) : t('header.notifications');

    return (
        <header className="top-header"> {/* Use new class name */}
            {/* Left side: Mimic Trigger (visual placeholder if needed, non-functional), Separator, Breadcrumb */}
            <div className="top-header-left">
                {/* Mobile off-canvas sidebar toggle — hidden by default via CSS,
                    shown only ≤768px (see TopHeader.scss), and never rendered
                    when the layout has no sidebar at all (e.g. FTUE). */}
                {!hideSidebarToggle && (
                    <button
                        type="button"
                        className="button-ghost header-button sidebar-mobile-toggle"
                        onClick={onToggleMobileSidebar}
                        aria-label={isMobileSidebarOpen ? t('header.closeMenu') : t('header.openMenu')}
                        aria-expanded={isMobileSidebarOpen}
                    >
                        {isMobileSidebarOpen ? <X /> : <Menu />}
                    </button>
                )}
                 {/* Visual Separator */}
                 {/* <div className="separator-vertical"></div> */}

                {/* Static Breadcrumb Example */}
                <nav className="breadcrumb" aria-label="breadcrumb">
                    <ol className="breadcrumb-list">
                        {breadcrumbItems.map((item, index) => (
                            <React.Fragment key={item.path}>
                                <li className={`breadcrumb-item ${index === breadcrumbItems.length - 1 ? 'current' : ''}`}>
                                    {index === breadcrumbItems.length - 1 ? (
                                        item.label
                                    ) : (
                                        <a href={item.path}>{item.label}</a>
                                    )}
                                </li>
                                {index < breadcrumbItems.length - 1 && (
                                    <li className="breadcrumb-separator" aria-hidden="true">/</li>
                                )}
                            </React.Fragment>
                        ))}
                    </ol>
                </nav>
            </div>

            {/* Center: Organization Selector (Admin only) - hidden until redesign */}

            {/* Right side: Actions (User Menu) */}
            <div className="top-header-right">
                {/* Streak Counter */}
                {streakCount !== null && (
                    <button
                        type="button"
                        className={`streak-pill${streakCount > 0 ? ' streak-pill--active' : ''}`}
                        onClick={() => navigate('/my-progress')}
                        aria-label={t('header.currentStreak', { count: streakCount })}
                    >
                        <span className="streak-flame-wrap">
                            <Flame className="streak-flame" fill="currentColor" />
                        </span>
                        <span className="streak-count">{streakCount}</span>
                        <span className="header-button-tooltip">{t('header.streakTooltip', { count: streakCount })}</span>
                    </button>
                )}


                {/* Tutorial Dropdown */}
                <div className="header-menu-container">
                    <button
                        type="button"
                        ref={tutorialButtonRef}
                        className="header-button"
                        id="tour-header-tutorial"
                        onClick={() => {
                            setIsTutorialMenuOpen((prev) => {
                                const opening = !prev;
                                if (opening) {
                                    setPageTourSteps(getPageSteps(location.pathname, pageTourDriverRef));
                                }
                                return opening;
                            });
                        }}
                        aria-label={t('common.tutorial')}
                        aria-haspopup="true"
                        aria-expanded={isTutorialMenuOpen}
                    >
                        <HelpCircle />
                        <span className="header-button-tooltip">{t('common.tutorial')}</span>
                    </button>

                    {isTutorialMenuOpen && (
                        <div ref={tutorialMenuRef} className="header-dropdown-menu">
                            <div className="dropdown-items">
                                <button
                                    type="button"
                                    id="tour-app-tutorial-option"
                                    className="header-menu-item"
                                    onClick={() => {
                                        startReferenceTour('reference-tour', getReferenceSteps());
                                        setIsTutorialMenuOpen(false);
                                    }}
                                >
                                    {t('common.appTutorial')}
                                </button>
                                <button
                                    type="button"
                                    id="tour-page-tutorial-option"
                                    className="header-menu-item"
                                    disabled={pageTourSteps.length === 0}
                                    onClick={() => {
                                        pageTourDriverRef.current = startReferenceTour('page-tour', pageTourSteps);
                                        setIsTutorialMenuOpen(false);
                                    }}
                                >
                                    {t('common.pageTutorial')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Feedback Button — opens FeedbackModal directly, no dropdown */}
                <div className="header-menu-container">
                    <button
                        type="button"
                        className="header-button"
                        id="tour-header-help"
                        onClick={() => setIsFeedbackModalOpen(true)}
                        aria-label={t('common.sendFeedback')}
                    >
                        <Bug />
                        <span className="header-button-tooltip">{t('common.sendFeedback')}</span>
                    </button>
                </div>

                {/* Notification Bell */}
                <div className="header-menu-container">
                    <button
                        type="button"
                        ref={notifButtonRef}
                        className="header-button notif-bell-button"
                        id="tour-header-bell"
                        onClick={handleBellClick}
                        aria-label={bellLabel}
                        aria-haspopup="true"
                        aria-expanded={isNotifOpen}
                    >
                        <Bell />
                        {unreadCount > 0 && (
                            <span className="notif-badge" aria-hidden="true">
                                {badgeLabel}
                            </span>
                        )}
                        <span className="header-button-tooltip">{t('header.notifications')}</span>
                    </button>

                    {isNotifOpen && (
                        <div ref={notifRef} className="notif-dropdown" role="region" aria-label={t('header.notifications')}>
                            <div className="notif-dropdown-header">
                                <span className="notif-dropdown-title">{t('header.notifications')}</span>
                                {unreadCount > 0 && (
                                    <button type="button" className="notif-mark-all-btn" onClick={markAllAsRead}>
                                        {t('header.markAllRead')}
                                    </button>
                                )}
                            </div>

                            <div className="notif-list" ref={notifListRef}>
                                {loading && (
                                    <div className="notif-empty" role="status">{t('common.loadingDots')}</div>
                                )}
                                {!loading && notifications.length === 0 && (
                                    <div className="notif-empty">{t('header.noNotifications')}</div>
                                )}
                                {!loading && notifications.length > 0 && (
                                    <ul className="notif-items" aria-label={t('header.notifications')}>
                                        {notifications.map((notif) => (
                                            <li key={notif.id}>
                                                <button
                                                    type="button"
                                                    className={`notif-item${notif.read ? '' : ' notif-item--unread'}`}
                                                    onClick={() => handleNotifClick(notif)}
                                                >
                                                    {!notif.read && <span className="notif-unread-dot" aria-hidden="true" />}
                                                    <div className="notif-item-body">
                                                        {!notif.read && <span className="sr-only">{t('header.unreadPrefix')} </span>}
                                                        <p className="notif-item-message">{translateNotificationMessage(notif.type, notif.message, t, i18n.language)}</p>
                                                        <span className="notif-item-time">
                                                            {formatNotificationTime(notif.created_at, t, i18n.language)}
                                                        </span>
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {loadingMore && (
                                    <div className="notif-load-more" role="status">{t('header.loadingMore')}</div>
                                )}
                                {!loadingMore && !hasMore && notifications.length > 0 && (
                                    <div className="notif-end">{t('header.allCaughtUp')}</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* User Dropdown */}
                <div className="header-menu-container">
                    <button
                        type="button"
                        ref={buttonRef}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="header-button user-button"
                        id="tour-header-user-menu"
                        aria-label={t('header.userMenu')}
                        aria-haspopup="true"
                        aria-expanded={isMenuOpen}
                    >
                        <CircleUser />
                        <span className="header-button-tooltip">{t('header.userMenu')}</span>
                    </button>

                    {isMenuOpen && (
                        <div ref={menuRef} className="header-dropdown-menu">
                            <div className="dropdown-header">
                                <span className="user-name">{currentUser?.display_name || t('common.user')}</span>
                                <span className="user-role">
                                    {getRoleLabel(currentUser?.role, t)}
                                    {isProxyActive && ` ${t('header.demoView')}`}
                                </span>
                            </div>
                            {realRole === 'super_admin' && (
                                <DemoProxySwitcher onSwitched={() => setIsMenuOpen(false)} />
                            )}
                            <div className="dropdown-items">
                                {canSeeSubscription && (
                                    <button
                                        type="button"
                                        onClick={() => { navigate('/subscription'); setIsMenuOpen(false); }}
                                        className="header-menu-item"
                                    >
                                        <CircleUser size={15} className="menu-item-icon" />
                                        {t('header.myProfile')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => { navigate('/profile'); setIsMenuOpen(false); }}
                                    className="header-menu-item"
                                >
                                    <UserCircle size={15} className="menu-item-icon" />
                                    {t('header.myProfile')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { navigate('/device-setup'); setIsMenuOpen(false); }}
                                    className="header-menu-item"
                                >
                                    <Settings2 size={15} className="menu-item-icon" />
                                    {t('onboarding.deviceSetup')}
                                </button>
                                <button
                                    type="button"
                                    onClick={async () => { await onLogout(); setIsMenuOpen(false); }}
                                    className="header-menu-item header-menu-item--logout"
                                >
                                    <LogOut size={15} className="menu-item-icon" />
                                    {t('header.logOut')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Feedback Modal */}
            <FeedbackModal
                isOpen={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
                rolePlaySettings={{
                    selections: rolePlaySelections,
                    componentDetails: componentDetails
                }}
            />

            {/* Badge-unlock celebration — rendered here (in the always-mounted layout header)
                so it surfaces on whatever page the user is currently on. Shown one at a time. */}
            {badgeQueue.length > 0 && (
                <BadgeUnlockPopup
                    key={badgeQueue[0].id}
                    badgeKey={badgeQueue[0].key}
                    onDismiss={() => setBadgeQueue((prev) => prev.filter((x) => x.id !== badgeQueue[0].id))}
                />
            )}
        </header>
    );
};

export default TopHeader;